import { GridColDef } from "@mui/x-data-grid";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { OrderOptimizedListResponse } from "./types";
import { OrderAction } from "../components/OrderAction";
import { ImagePreviewBox } from "../../../components/layout/slides/ImagePreview";
import { OrderActionsMenu } from "../components/OrderActionsMenu";
import { Flag, OpenInNew } from "@mui/icons-material";
import { Copyable } from "../../../components/copyable/Copyable";

const BASE_TRANSLATION_KEY = "list.fields";

export type TabColumnInfo = {
  filterField: string;
  path: string;
  hasRowSelection?: boolean;
  columns?: string[];
};

export const standartColumns = [
  "detail",
  "images",
  "id",
  "customerInfo",
  "orderInfo",
  "created",
  "flag",
  "barcode",
  "status",
  "actions",
];

export const tabColumnMapping: TabColumnInfo[] = [
  {
    filterField: "all",
    path: "all",
    columns: standartColumns,
  },
  {
    filterField: "requiresAction",
    path: "requiresAction",
    columns: standartColumns,
  },
  {
    filterField: "toBePurchased",
    path: "toBePurchased",
    columns: standartColumns,
  },
  {
    filterField: "delayedPurchase",
    path: "delayedPurchase",
    columns: standartColumns,
  },
];

const isOrderDelayed = (order: OrderOptimizedListResponse): boolean => {
  const deliveryOrderStatus = order.deliveryOrder.deliveryOrderStatus;

  const purchaseDate = new Date(order.deliveryOrder.purchaseDate).getTime();

  const twelveHoursAgo = Date.now() - 12 * 60 * 60 * 1000;

  const allUnprogressed = order.supplyOrders.every(
    (supplyOrder) =>
      supplyOrder.supplyOrderStatus === "PENDING" ||
      supplyOrder.supplyOrderStatus === "PAYMENT_DECLINED",
  );

  const noProgressedOrders = order.supplyOrders.length === 0 || allUnprogressed;

  return (
    (deliveryOrderStatus === "ACCEPTED" || deliveryOrderStatus === "PENDING") &&
    noProgressedOrders &&
    purchaseDate < twelveHoursAgo
  );
};

export const getOrderListColumns = (
  translate: (_key: string) => string,
  navigate: (_path: string) => void,
  locales: Intl.LocalesArgument,
): GridColDef<OrderOptimizedListResponse>[] => [
  {
    field: "detail",
    headerName: translate("list.fields.details"),
    minWidth: 140,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      const url = `/order-detail/${params.row.id}`;

      return (
        <Button
          variant="contained"
          size="small"
          endIcon={<OpenInNew />}
          onClick={(e) => {
            e.stopPropagation();

            // sadece sol click
            if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              navigate(url);
            }
          }}
          component="a"
          href={url}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 1.5,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          {translate("list.fields.toDetail")}
        </Button>
      );
    },
  },
  {
    field: "id",
    headerName: translate(`${BASE_TRANSLATION_KEY}.orderId`),
    minWidth: 150,
    renderCell: (params) => {
      if (!params.value) return null;
      return <Copyable initialLabel={params.value} />;
    },
  },

  {
    field: "images",
    headerName: translate(`${BASE_TRANSLATION_KEY}.images`),
    minWidth: 150,
    sortable: false,
    renderCell: (params) => {
      return (
        <ImagePreviewBox
          images={params.value}
          itemCount={params.row.orderItems.length}
        />
      );
    },
  },

  {
    field: "customerInfo",
    sortable: false,
    headerName: translate(`${BASE_TRANSLATION_KEY}.amazonOrderId`),
    flex: 1,
    minWidth: 150,
    renderCell: (params) => {
      const order = params.row;
      const amazonOrderId = order.amazonOrderId;
      return <Copyable initialLabel={amazonOrderId} />;
    },
  },
  {
    field: "orderInfo",
    headerName: translate(`${BASE_TRANSLATION_KEY}.orderInfo`),
    minWidth: 220,
    sortable: false,
    renderCell: (params) => {
      const order = params.row;
      const orderCount =
        order.deliveryOrder.numberOfItemsShipped +
        order.deliveryOrder.numberOfItemsUnshipped;
      const orderTotal = order.deliveryOrder.orderTotalAmount;
      const totalCurrency = order.deliveryOrder.orderTotalCurrency;
      const orderCountLabel = translate(`${BASE_TRANSLATION_KEY}.orderCount`);
      const orderTotalLabel = translate(`${BASE_TRANSLATION_KEY}.orderTotal`);
      const quantityBought = order.orderItems.reduce(
        (sum, item) => sum + (item.quantityBought ?? 0),
        0,
      );

      return (
        <Box
          display="flex"
          gap={0.5}
          justifyContent="center"
          height="100%"
          flexDirection="column"
        >
          <Box
            sx={{
              display: "flex",

              borderRadius: "4px",
              padding: "0 4px",
              width: "fit-content",
              border: "1px solid",
              borderColor: (theme) => theme.palette.primary.main,
            }}
          >
            <Typography variant="caption" fontSize={14} color="primary">
              {orderCountLabel}: {orderCount}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              color: "	#2E8B57",
              borderRadius: "4px",
              padding: "0 4px",
              width: "fit-content",
              border: "1px solid #2E8B57",
            }}
          >
            <Typography variant="caption" fontSize={14}>
              {orderTotalLabel}:{" "}
              {orderTotal ? orderTotal + " " + totalCurrency : "-"}
            </Typography>
          </Box>
          {quantityBought != 0 && (
            <Typography fontWeight="bold" color="success">
              {translate("list.fields.quantityBought")}: {quantityBought}
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    field: "created",
    minWidth: 220,
    flex: 1,
    headerName: translate(`${BASE_TRANSLATION_KEY}.purchaseDate`),
    renderCell: (params) => {
      const order = params.row;
      const isDelayedPurchase = isOrderDelayed(order);

      const purchaseDate = order.deliveryOrder.purchaseDate;

      const date = new Date(purchaseDate).toLocaleDateString(locales);
      const time = new Date(purchaseDate).toLocaleTimeString(locales);

      return (
        <Box
          justifyContent="center"
          display="flex"
          flexDirection="column"
          height="100%"
        >
          <Typography>
            {date} {time}
          </Typography>
          {isDelayedPurchase && (
            <Typography
              variant="caption"
              color="error"
              flexWrap="wrap"
              sx={{
                width: 200,
                display: "inline-block",
                whiteSpace: "pre-line",
              }}
            >
              {translate("list.delayed.dateAlert")}
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    field: "flag",
    headerName: translate(`${BASE_TRANSLATION_KEY}.flag`),
    sortable: false,
    flex: 1,
    renderCell: (params) => {
      return (
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"flex-start"}
          height="100%"
          spacing={1}
        >
          {params?.row?.orderFlag && (
            <Tooltip title={params.row.orderFlag.flagName}>
              <Chip
                label={params.row.orderFlag.flagName}
                icon={
                  <Flag
                    sx={{
                      color: `${params.row.orderFlag.flagColor} !important`,
                      fontSize: "1.3rem",
                    }}
                  />
                }
                size="medium"
                sx={{
                  backgroundColor: `${params.row.orderFlag.flagColor}18`,
                  border: "1px solid",
                  borderColor: `${params.row.orderFlag.flagColor}40`,
                  color: params.row.orderFlag.flagColor,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  height: 32,
                  borderRadius: "16px",
                }}
              />
            </Tooltip>
          )}
        </Stack>
      );
    },
  },
  {
    field: "barcode",
    headerName: translate(`${BASE_TRANSLATION_KEY}.barcode`),
    minWidth: 1,
    sortable: false,
    renderCell: (params) => {
      return params.row.barcode ? (
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          spacing={1}
        >
          <Copyable initialLabel={params.row.barcode} />
        </Stack>
      ) : (
        <>-</>
      );
    },
  },
  {
    field: "status",
    headerName: translate(`${BASE_TRANSLATION_KEY}.status.title`),
    minWidth: 180,
    sortable: false,
    renderCell: (params) => {
      if (
        params.row.orderStatus === "WAITING_FOR_ADDRESS" ||
        params.row.orderStatus === "WAITING_FOR_PURCHASE"
      ) {
        return <OrderAction order={params.row} />;
      } else if (params.row.orderStatus === "WAREHOUSE_ERROR") {
        return params.row.warehouseErrorMessage;
      } else {
        return translate(
          `${BASE_TRANSLATION_KEY}.status.${params.row.orderStatus}`,
        );
      }
    },
  },
  {
    field: "actions",
    headerName: translate(`${BASE_TRANSLATION_KEY}.actions.title`),
    minWidth: 1,
    sortable: false,
    renderCell: (params) => {
      return <OrderActionsMenu row={params.row} />;
    },
  },
];
